import React, { memo, Suspense, useEffect, useCallback, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { format, parse } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import useTracking from '@gameday/utils/hooks/useTracking';
import lockViewAction from '@gameday/app/lib/store/actions/lockViewAction';
import MlbNativeWrapper from '@gameday/utils/MlbNativeWrapper';
import { getFVRuntimeState } from '../store/selectors/getFieldVision';
import { FieldVisionPlayerContainer } from '../fieldvision.style';

// const FieldVisionPlayer = React.lazy(() => import('@bvg/poser')); // for local dev
const FieldVisionPlayer = React.lazy(() => import(/* webpackChunkName: "@bvg_poser" */ '@bvg/poser'));
const FieldVisionPlayerFallback = React.lazy(() => import(/* webpackChunkName: "@bvg_poser-fallback" */ '@bvg/poser-fallback'));

let seekTo = null;
let debugFV = false;
let fpsFV = false;
let useFallbackFVOverride = null;

if (typeof URLSearchParams !== 'undefined' && typeof location !== 'undefined') {
    const params = new URLSearchParams(location.search);
    seekTo = params.get('tfs') ? format(parse(params.get('tfs'), 'yyyyMMdd_HHmmss', new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'") : null;
    debugFV = params.get('debugFV') === 'true';
    fpsFV = params.get('fpsFV') === 'true';
    // If the param is present, use it to override the config value
    if (params.get('forceFallbackFV')) {
        useFallbackFVOverride = params.get('forceFallbackFV') === 'true';
    }
}

const GamedayFieldVisionPlayer = forwardRef(({
    gamePk,
    accessToken,
    debug = false,
    setIsFVFullscreen,
    setTutorialOpen,
    onGumboChange,
    playId,
    embeddedControls,
    defaultCam = 'strikezone',
    autoPlay = true,
    loop = false,
    cameraIntro = false
}, controllerRef) => {
    const containerRef = useRef(null);
    const Tracking = useTracking();
    const dispatch = useDispatch();
    const { batEnabled, useFallbackFV } = useSelector(getFVRuntimeState);
    const typeOf3DContent = playId ? 'Replay' : 'Live';

    const onCanPlay = useCallback((canIPlay) => {
        if (controllerRef.current) {
            controllerRef.current?.setCameraMode(defaultCam);
            if (autoPlay) {
                controllerRef.current?.play();
            }
            MlbNativeWrapper.then((results) => {
                const { stopTrace } = results;
                stopTrace('gameday: fv launch');
            });
        }
    }, [controllerRef]);

    const onCameraChange = useCallback((change) => {
        if (change?.mode == 'free' && change?.fromInteraction == true) {
            Tracking.track.track_click({
                componentName: [`Gameday 3D ${typeOf3DContent}`],
                text: 'Offtrack',
                activity: 'Camera Off Track'
            });
        } else {
            Tracking.track.track_click({
                componentName: [`Gameday 3D ${typeOf3DContent}`],
                text: change?.label || '',
                activity: 'Camera Button Click'
            });
        }
    }, [controllerRef, typeOf3DContent]);

    const onFullscreen = useCallback((enteringFullscreen, fireTracking = true) => {
        if (setIsFVFullscreen) {
            setIsFVFullscreen(enteringFullscreen);
        }
        dispatch(lockViewAction({ lockView: enteringFullscreen }));
        if (fireTracking) {
            const enterOrExit = enteringFullscreen ? 'Entering' : 'Exiting';
            Tracking.track.track_click({
                componentName: [`Gameday 3D ${typeOf3DContent}`],
                text: enterOrExit + ' fullscreen',
                activity: 'Fullscreen'
            });
        }
    }, [controllerRef, typeOf3DContent]);

    const onInfoButton = useCallback(() => {
        if (setTutorialOpen) {
            setTutorialOpen(val => !val);
        }
    }, [controllerRef]);

    // empty array parameter ensures this `useEffect` is only run once.
    useEffect(() => {
        return function cleanup () {
            if (controllerRef.current) {
                // Remove lockstate, and don't fire tracking
                onFullscreen(false, false);
            }
        };
    }, []);

    // If an override param is supplied, use that value to determine if we should use the fallback player
    // Otherwise, use the value from the config
    const FieldVisionPlayerDecision = (useFallbackFVOverride ?? useFallbackFV) ? FieldVisionPlayerFallback : FieldVisionPlayer;

    return (
        <FieldVisionPlayerContainer ref={containerRef}>
            <Suspense fallback={<></>}>
                <FieldVisionPlayerDecision
                    containerRef={containerRef}
                    controllerRef={controllerRef}
                    options={{
                        gamePk: gamePk * 1,
                        debug: debugFV,
                        fps: fpsFV,
                        playId,
                        embeddedControls,
                        bearerToken: accessToken,
                        loop,
                        batEnabled,
                        clientIdentifier: 'gameday',
                        callbacks: {
                            onGumboChange,
                            onCameraChange,
                            onCanPlay,
                            onFullscreen,
                            onInfoButton
                        },
                        key: gamePk + accessToken,
                        seekTo,
                        cameraIntro
                    }}
                />
            </Suspense>
        </FieldVisionPlayerContainer>
    );
});

GamedayFieldVisionPlayer.displayName = 'GamedayFieldVisionPlayer';

GamedayFieldVisionPlayer.propTypes = {
    gamePk: PropTypes.number.isRequired,
    accessToken: PropTypes.string.isRequired,
    debug: PropTypes.bool,
    setIsFVFullscreen: PropTypes.func,
    setTutorialOpen: PropTypes.func,
    onGumboChange: PropTypes.func,
    playId: PropTypes.string,
    embeddedControls: PropTypes.bool,
    defaultCam: PropTypes.string,
    autoPlay: PropTypes.bool,
    loop: PropTypes.bool,
    cameraIntro: PropTypes.bool
};

export default memo(GamedayFieldVisionPlayer);
